import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Gift, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useBrandName } from '../context/SettingsContext';
import { submitContact } from '../lib/api';
import { BACKEND_URL, BTN_PRIMARY, BTN_OUTLINE, RADIUS_SURFACE } from '../config/brand';
import EditableText from './EditableText';

const STORAGE_KEY = 'welcomePopupDismissed';

const WelcomePopup = () => {
  const brandName = useBrandName();
  const location = useLocation();
  const onBookingTunnel = location.pathname.startsWith('/reserver');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const { toast } = useToast();

  useEffect(() => {
    if (onBookingTunnel) {
      setIsOpen(false);
      return undefined;
    }
    if (sessionStorage.getItem(STORAGE_KEY)) return undefined;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, [onBookingTunnel]);

  const handleClose = () => {
    setIsOpen(false);
    setIsExpanded(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!BACKEND_URL) {
      toast({
        title: 'Erreur',
        description: 'Service indisponible. Réessayez plus tard.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: 'atelier-decouverte',
        message: "Demande d'atelier découverte offert",
      });
      toast({
        title: 'Demande enregistrée',
        description: 'Nous vous recontacterons sous 24h.',
      });
      handleClose();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message || "Impossible d'envoyer votre demande.",
        variant: 'destructive',
      });
    }
  };

  if (!isOpen || onBookingTunnel) return null;

  return (
    <div className="fixed bottom-8 right-4 z-[90] max-w-sm w-[calc(100%-2rem)] sm:w-96 animate-in slide-in-from-bottom-4 fade-in duration-500">
      <div className={`relative bg-white/95 backdrop-blur-md border border-[rgba(58,0,224,0.18)] ${RADIUS_SURFACE} shadow-2xl shadow-[0_16px_40px_-16px_rgba(58,0,224,0.28)] overflow-hidden`}>
        <div
          className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#3a00e0] via-[#fab03a] to-[#fab03a]"
          aria-hidden="true"
        />
        {/* Compact header — always visible */}
        <div className="p-4 flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-lg bg-[rgba(250,176,58,0.18)] border border-[rgba(250,176,58,0.45)] flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            <Gift className="h-4 w-4 text-[#c47a0a]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#c47a0a] mb-0.5">
              <EditableText
                editKey="welcome.popup.badge"
                defaultValue="Bonne rentrée !"
                as="span"
              />
            </p>
            <p className="text-slate-900 font-semibold text-sm leading-snug">
              <EditableText
                editKey="welcome.popup.title"
                defaultValue="Atelier découverte offert"
                as="span"
              />
            </p>
            <p className="text-slate-600 text-xs mt-0.5">
              <EditableText
                editKey="welcome.popup.subtitle"
                defaultValue={`Testez ${brandName} sans engagement`}
                as="span"
              />
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors shrink-0"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Expandable form */}
        {isExpanded ? (
          <form onSubmit={handleSubmit} className="px-4 pb-4 space-y-3 border-t border-slate-200 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor="popup-name" className="text-slate-600 text-xs">
                Nom complet *
              </Label>
              <Input
                id="popup-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-9"
                placeholder="Votre nom"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="popup-email" className="text-slate-600 text-xs">
                Email *
              </Label>
              <Input
                id="popup-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-9"
                placeholder="votre@email.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="popup-phone" className="text-slate-600 text-xs">
                Téléphone *
              </Label>
              <Input
                id="popup-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-9"
                placeholder="06 XX XX XX XX"
              />
            </div>
            <Button type="submit" className={`w-full ${BTN_PRIMARY}`}>
              <EditableText editKey="welcome.popup.cta_submit" defaultValue="Être recontacté sous 24h" as="span" />
            </Button>
            <p className="text-[10px] text-slate-500 text-center leading-relaxed">
              En soumettant, vous acceptez d&apos;être recontacté par {brandName}.
            </p>
          </form>
        ) : (
          <div className="px-4 pb-4 flex gap-2">
            <Button
              type="button"
              className={`flex-1 ${BTN_PRIMARY}`}
              onClick={() => setIsExpanded(true)}
            >
              <EditableText editKey="welcome.popup.cta_expand" defaultValue="En savoir plus" as="span" />
            </Button>
            <Link to="/reserver" onClick={handleClose} className="flex-1">
              <Button type="button" variant="outline" className={`w-full ${BTN_OUTLINE}`}>
                <EditableText editKey="welcome.popup.cta_reserve" defaultValue="Réserver" as="span" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePopup;
